import React from 'react';

import { Box, Button, Drawer, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FilterListIcon } from 'assets/icons/filter.svg';
import ColoredButton from 'components/ColoredButton';
import safeT from 'helpers/safeT/safeT';
import useBoolState from 'hooks/useBoolState';
import general_messages from 'messages/general_messages';

import FilterChip from './_components/FilterChip';

const useStyles = makeStyles(theme => ({
  wrapper: {
    borderBottom: `3px solid ${theme.palette.primary[100]}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2, 1, 0),
  },
  filtersTab: {
    minWidth: '220px',
    maxWidth: '320px',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    [theme.breakpoints.down('sm')]: {
      ...theme.mixins.toolbarPadding,
    },
  },
  hideWrapper: {
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    padding: theme.spacing(2),
    background: theme.palette.common.white,
    zIndex: 1,
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridGap: theme.spacing(2),
    justifyItems: 'start',
  },
  filtersTabWrapper: {
    padding: theme.spacing(0, 2, 2, 2),
    gridGap: theme.spacing(2),
    display: 'grid',
    justifyItems: 'start',
  },
  filterButton: {
    height: '41px',
    width: '41px',
    paddingTop: '14px',
    paddingBottom: '10px',
    boxShadow: theme.shadows[2],
  },
  filtersSection: {
    marginLeft: theme.spacing(2),
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  filtersSectionPlaceholder: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `3px solid ${theme.palette.warning.main}`,
  },
  placeholderBadge: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(0.5),
  },
  buttonWrapper: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  filtersCount: {
    fontWeight: 700,
    fontSize: '0.7rem',
    color: theme.palette.common.white,
    position: 'absolute',
    top: '-5px',
    right: '0px',
    backgroundColor: theme.palette.info.main,
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
    pointerEvents: 'none',
  },
  hideButton: {
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    '&.Mui-focusVisible, &:focus-visible': {
      outline: `3px solid ${theme.palette.grey[300]}`,
      outlineOffset: 2,
    },
  },
}));

const Filters = ({ filters, setFilters, availableFilters, withTab }) => {
  const { t } = useTranslation();

  const addFilter = ({ sectionKey, ...rest }) => {
    if (!availableFilters?.[sectionKey] || availableFilters[sectionKey].isDisabled) return;
    const { isSingleSelect } = availableFilters[sectionKey];
    setFilters(prevState => {
      const newState = { ...prevState };
      if (!newState[sectionKey]) newState[sectionKey] = [];

      // TODO add isSingleSelect handling for parser
      if (isSingleSelect) newState[sectionKey] = [rest];
      else newState[sectionKey] = [...newState[sectionKey], rest];

      return newState;
    });
  };

  const deleteFilter = ({ id, sectionKey }) => {
    setFilters(prevState => {
      const newState = { ...prevState };
      newState[sectionKey] = newState[sectionKey].filter(selected => selected.id !== id);
      return newState;
    });
  };
  const clearAllFilters = () => setFilters({});

  const { state: isFilersTabOpen, setFalse: closeFilersTab, setTrue: openFiltersTab } = useBoolState(false);

  const styles = useStyles();

  const filtersCount = filters ? Object.values(filters).reduce((acc, curr) => acc + (curr?.length || 0), 0) : 0;

  return (
    <div>
      {withTab ? (
        <div className={styles.wrapper}>
          <Box flexGrow={1}>
            {filters &&
              Object.entries(filters)
                .filter(([sectionKey]) => sectionKey !== 'purpose')
                .map(([sectionKey, filterChips]) =>
                  filterChips.map(({ name, id }) => (
                    <FilterChip
                      key={id}
                      deleteFilter={deleteFilter}
                      id={id}
                      isSelected
                      name={Array.isArray(name) ? t(...name) : name}
                      sectionKey={sectionKey}
                    />
                  )),
                )}
          </Box>
          <ColoredButton
            aria-label={t(...general_messages.open_filters)}
            className={styles.filterButton}
            customColor='secondary'
            icon
            onClick={openFiltersTab}
          >
            <FilterListIcon />
          </ColoredButton>
        </div>
      ) : (
        <div className={styles.buttonWrapper}>
          <ColoredButton
            aria-label={t(...general_messages.open_filters)}
            className={styles.filterButton}
            customColor='secondary'
            icon
            onClick={openFiltersTab}
          >
            <FilterListIcon />
          </ColoredButton>
          {filtersCount > 0 && <span className={styles.filtersCount}>{filtersCount}</span>}
        </div>
      )}

      <Drawer
        anchor='right'
        classes={{
          paper: styles.filtersTab,
        }}
        onClose={closeFilersTab}
        open={isFilersTabOpen}
        PaperProps={{
          'aria-label': t(...general_messages.filters),
          elevation: 3,
          role: 'dialog',
        }}
        style={{ zIndex: 1250 }}
      >
        <div className={styles.hideWrapper}>
          <ColoredButton
            aria-label={t(...general_messages.close_filters)}
            className={styles.filterButton}
            customColor='secondary'
            icon
            onClick={closeFilersTab}
          >
            <FilterListIcon />
          </ColoredButton>
          <Typography className={styles.hideButton} component='button' onClick={closeFilersTab} type='button' variant='button'>
            {t(...general_messages.hide)}
          </Typography>
        </div>
        <div className={styles.filtersTabWrapper}>
          <Button onClick={clearAllFilters}>{t(...general_messages.clear_all)}</Button>
          {availableFilters &&
            Object.entries(availableFilters)
              .filter(([, value]) => value != null)
              .map(([key, { sectionName, filters: filtersToRender, isPlaceholder, placeholderLabel, isDisabled, disabledTooltip }]) => (
                <div
                  key={key}
                  className={isPlaceholder ? [styles.filtersSection, styles.filtersSectionPlaceholder].join(' ') : styles.filtersSection}
                >
                  {sectionName && sectionName.length > 0 && (
                    <Typography component='h3' variant='subtitle1'>
                      {t(...sectionName)}
                    </Typography>
                  )}
                  <div>
                    {Array.isArray(filtersToRender) &&
                      filtersToRender.map(({ id, name, tooltip }) => {
                        const chipName = Array.isArray(name) ? safeT(t, ...name) : name;
                        const chipTooltip = Array.isArray(tooltip) ? safeT(t, ...tooltip) : tooltip;
                        const resolvedTooltip = isDisabled && disabledTooltip?.length ? t(...disabledTooltip) : chipTooltip;

                        return (
                          <FilterChip
                            key={id}
                            addFilter={addFilter}
                            deleteFilter={deleteFilter}
                            disabled={isDisabled}
                            filterTab
                            id={id}
                            isSelected={filters[key] && filters[key].findIndex(selected => selected.id === id) >= 0}
                            name={chipName}
                            sectionKey={key}
                            tooltip={resolvedTooltip}
                          />
                        );
                      })}
                  </div>
                  {isPlaceholder && placeholderLabel && placeholderLabel.length > 0 && (
                    <Typography className={styles.placeholderBadge} variant='caption'>
                      {t(...placeholderLabel)}
                    </Typography>
                  )}
                </div>
              ))}
        </div>
      </Drawer>
    </div>
  );
};

const FiltersPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  }),
);

Filters.propTypes = {
  availableFilters: PropTypes.objectOf(
    PropTypes.shape({
      sectionName: PropTypes.arrayOf(PropTypes.string),
      filters: FiltersPropTypes,
      isSingleSelect: PropTypes.bool,
      isPlaceholder: PropTypes.bool,
      placeholderLabel: PropTypes.arrayOf(PropTypes.string),
      isDisabled: PropTypes.bool,
      disabledTooltip: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  filters: PropTypes.objectOf(FiltersPropTypes),
  setFilters: PropTypes.func.isRequired,
  withTab: PropTypes.bool,
};

Filters.defaultProps = {
  filters: null,
  availableFilters: null,
  withTab: false,
};

export default Filters;
